import React, { useCallback, useEffect, useRef } from 'react';
import Select from 'react-select';
import clsx from 'clsx';
import Papa from 'papaparse';
import { twMerge } from 'tailwind-merge';

import { CheckboxArea, DateRange } from './Inputs';
import { Gears } from '../../icons';

export function PlotAdjustForm({
  fieldsData,
  saveSelectedValues,
  formSubmitRequest,
  onSubmitSuccess,
  isLoading,
  setPlotIsLoading,
  customFields,
  autoSubmit,
  className,
}) {
  const formRef = useRef(null);

  function handleSubmit(event) {
    event.preventDefault();
    submitForm(event.target);
  }
  async function submitForm(form) {
    setPlotIsLoading(true);

    const formData = new FormData(form);

    try {
      const data = await formSubmitRequest(formData);
      onSubmitSuccess(data);
    } catch (e) {
      console.error('Plot generation failed:', e);
    } finally {
      setPlotIsLoading(false);
    }
  }

  useEffect(() => {
    if (autoSubmit) {
      submitForm(formRef.current);
    }
  }, [fieldsData]);

  return (
    <>
      <form
        action=""
        onSubmit={handleSubmit}
        className={twMerge('flex flex-col', className)}
        ref={formRef}
      >
        <h2 className="mt-8 mb-0">Dostosuj</h2>
        <FieldRenderer
          fieldsData={fieldsData}
          saveSelectedValues={saveSelectedValues}
        />
        {customFields}
        <GenerateButton isLoading={isLoading} />
      </form>
    </>
  );
}

function GenerateButton({ isLoading }) {
  return (
    <button
      type="submit"
      className={clsx(
        'btn btn-success btn-md mt-8',
        isLoading && 'btn-disabled !text-gray-100/50',
      )}
    >
      Wygeneruj
      <Gears className={'w-6'} />
    </button>
  );
}

export function ExportForm({ svgBlob, isLoading }) {
  return (
    <>
      <h2 className="text-white">Eksport</h2>
      <a
        href={svgBlob && URL.createObjectURL(svgBlob)}
        aria-disabled={!svgBlob}
        className={clsx(
          'btn btn-success mt-6',
          isLoading && 'btn-disabled !text-gray-100/50',
        )}
        download
      >
        Zapisz SVG
      </a>
    </>
  );
}

export function ExportFormCsvOnly({ csvData, isLoading }) {
  const csv = csvData && Papa.unparse(csvData);
  const blob = csv && new Blob([csv], { type: 'text/csv;charset=utf-8' });

  return (
    <>
      <h2 className="text-white">Eksport</h2>
      <a
        href={blob && URL.createObjectURL(blob)}
        aria-disabled={!blob}
        className={clsx(
          'btn btn-success mt-6',
          isLoading && 'btn-disabled !text-gray-100/50',
        )}
        download="Ocena modelu - metryki.csv"
      >
        Zapisz csv
      </a>
    </>
  );
}

export function FieldRenderer({ fieldsData, saveSelectedValues }) {
  console.log('fieldsData', fieldsData);
  return (
    <>
      {fieldsData?.map((field) => (
        <div key={field.form_name} className="flex flex-col items-stretch">
          {field.type == 'select' && (
            <>
              <h3>{field.label}</h3>
              <select
                type="select"
                className="select select-sm"
                name={field.form_name}
                {...(saveSelectedValues === undefined
                  ? {
                      defaultValue: field.default_value,
                    }
                  : {
                      value: field.default_value,
                      onChange: (event) =>
                        saveSelectedValues(field.form_name, event.target.value),
                    })}
                aria-label={field.label}
              >
                {field.values.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </>
          )}
          {field.type == 'multiselect-checkbox' && (
            <>
              <h3>{field.label}</h3>
              <CheckboxArea
                checkboxFormName={field.form_name}
                values={field.values}
                defaultValues={field.default_value}
                minSelected={field.min_allowed}
              />
            </>
          )}
          {field.type == 'multiselect' && (
            <>
              <h3 id={`${field.form_name}-label`}>{field.label}</h3>
              <Select
                options={field.values}
                {...(saveSelectedValues === undefined
                  ? {
                      defaultValue: field.default_value,
                    }
                  : {
                      value: field.default_value,
                      onChange: (newValue) =>
                        saveSelectedValues(field.form_name, newValue),
                    })}
                isOptionDisabled={(option, selectValue) =>
                  field.max_allowed
                    ? selectValue.length >= field.max_allowed
                    : false
                }
                isMulti
                aria-labelledby={`${field.form_name}-label`}
                aria-label={field.label}
                className="react-select-container"
                classNamePrefix="react-select"
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    // nearest colors from tailwind
                    primary: '#3B82F6',
                    primary75: '#60A5FA',
                    primary50: '#BFDBFE',
                    primary25: '#DBEAFE',
                    danger: '#460809',
                    dangerLight: '#FDA4AF',
                  },
                })}
                name={field.form_name}
                required={field.min_allowed > 0}
              />
            </>
          )}
          {field.type == 'date_range' && (
            <>
              <h3>{field.label}</h3>
              <DateRange
                name={field.form_name}
                defaults={field.default_value}
                limits={{ min: field.min_value, max: field.max_value }}
                label={field.label}
              />
            </>
          )}
          {field.type == 'text' && (
            <>
              <h3>{field.label}</h3>
              <input
                className="input input-sm"
                name={field.form_name}
                {...(saveSelectedValues === undefined
                  ? {
                      defaultValue: field.default_value?.trim(),
                    }
                  : {
                      value: field.default_value,
                      onChange: (event) =>
                        saveSelectedValues(field.form_name, event.target.value),
                    })}
                placeholder="Domyślna wartość"
              ></input>
            </>
          )}
        </div>
      ))}
    </>
  );
}
